/**
 * Mid-hand resume snapshots.
 *
 * One row per tournament keyed by `tournamentId`. The gameStore writes a
 * snapshot after every reducer step so a refresh / disconnect lands the
 * player back at the exact `pendingActor`. When the hand finishes the row
 * is deleted (or `handState` set to null at hand boundary) and the final
 * HH is appended via handHistory.saveHand.
 */
import type { Event, HandState, TournamentState } from '@/engine/types';
import { getDb, type PokerDb, RESUME_SCHEMA_VERSION, type ResumeSnapshot } from './db';

function resolveDb(db?: PokerDb): PokerDb {
  return db ?? getDb();
}

export type ResumePayload = {
  tournamentState: TournamentState;
  handState: HandState | null;
  events: ReadonlyArray<Event>;
};

export async function saveResumeSnapshot(
  tournamentId: string,
  payload: ResumePayload,
  db?: PokerDb,
): Promise<void> {
  const snapshot: ResumeSnapshot = {
    tournamentId,
    schemaVersion: RESUME_SCHEMA_VERSION,
    tournamentState: payload.tournamentState,
    handState: payload.handState,
    events: payload.events,
    updatedAt: Date.now(),
  };
  await resolveDb(db).resumeSnapshots.put(snapshot);
}

export async function loadResumeSnapshot(
  tournamentId: string,
  db?: PokerDb,
): Promise<ResumeSnapshot | null> {
  const row = await resolveDb(db).resumeSnapshots.get(tournamentId);
  if (!row) return null;
  if (row.schemaVersion !== RESUME_SCHEMA_VERSION) {
    // Stale snapshot from an older schema — caller treats as "no snapshot".
    return null;
  }
  return row;
}

export async function clearResumeSnapshot(tournamentId: string, db?: PokerDb): Promise<void> {
  await resolveDb(db).resumeSnapshots.delete(tournamentId);
}

export async function listOpenResumes(db?: PokerDb): Promise<ResumeSnapshot[]> {
  return resolveDb(db).resumeSnapshots.orderBy('updatedAt').reverse().toArray();
}
